// --- Dependencies -------------------------------------------------------------------------------
import { Link } from "react-router-dom";
// --- Our Modules --------------------------------------------------------------------------------
import useLang from "@/hooks/useLang";
import useTranslation from "@/hooks/useTranslation";
import type { TyInstallmentPlan } from "@/types";
import { formatDate, formatMoney } from "@/utils";
import Avatar from "@/UI/atoms/Avatar";
import Icon from "@/UI/atoms/Icon";
import "./styles.css";
// --- Logic --------------------------------------------------------------------------------------
type Props = {
    plan: TyInstallmentPlan;
    onPay?: () => void;
    /** Opens the whole schedule on a timeline — the rows this card only summarises as a bar. */
    onSchedule?: () => void;
    hidePerson?: boolean;
};

/**
 * One agreement to pay over time — the whole of it, where an installment card is one row of it.
 *
 * The two views answer different questions and that is why the tab has both: *what is owed this
 * month* is a list of installments across every customer, and *how is this plan going* is this.
 * So the figure here is **progress**, not a date: paid against total, with a bar, because the only
 * thing anybody asks of a running plan is how much of it is left.
 */
export default function PlanCard({ plan, onPay, onSchedule, hidePerson }: Props) {
    const t = useTranslation();
    const lang = useLang((state) => state.lang) ?? "ar";

    const customer = typeof plan.customer === "object" && plan.customer ? plan.customer : null;
    const unit = typeof plan.unit === "object" && plan.unit ? plan.unit.number : null;

    const total = plan.totalAmount ?? 0;
    const paid = plan.paidAmount ?? 0;
    const left = Math.max(total - paid, 0);
    // Guarded: a plan with no total would divide by zero and render a bar of `NaN%`.
    const share = total > 0 ? Math.min(Math.round((paid / total) * 100), 100) : 0;

    return (
        <article className="plan-card" data-status={plan.status}>
            <div className="plan-card__top">
                <span className="plan-card__mark" aria-hidden="true">
                    <Icon name="stat" size={16} />
                </span>

                <div className="plan-card__identity">
                    <span className="plan-card__title">{plan.title || unit || t.sales.plans}</span>
                    <span className="plan-card__which">
                        {plan.count ? `${plan.count} · ${t.sales.installments}` : ""}
                        {plan.startAt ? ` · ${formatDate(plan.startAt, lang)}` : ""}
                    </span>
                </div>

                <span className="plan-card__state">{t.sales.planStatuses[plan.status]}</span>
            </div>

            {!hidePerson && customer && (
                <Link className="plan-card__person" to={`/crm/customers/${customer._id}`}>
                    <Avatar name={customer.name} picture={customer.picture ?? null} size={26} rounded />
                    <span className="plan-card__name">{customer.name}</span>
                    <Icon name="eye" size={14} />
                </Link>
            )}

            {/* How much of it is left, which is the only thing asked of a running plan. */}
            <div className="plan-card__progress">
                <div className="plan-card__bar">
                    <span className="plan-card__fill" style={{ width: `${share}%` }} />
                </div>
                <div className="plan-card__figures">
                    <span>
                        {t.sales.paid} <bdi dir="ltr">{formatMoney(paid)}</bdi>
                    </span>
                    <span>
                        {t.sales.remaining} <bdi dir="ltr">{formatMoney(left)}</bdi>
                    </span>
                </div>
            </div>

            {(onSchedule || (onPay && plan.status === "active")) && (
                <div className="plan-card__actions">
                    {onSchedule && (
                        <button type="button" className="plan-card__schedule" onClick={onSchedule}>
                            <Icon name="calendar" size={14} />
                            <span>{t.sales.viewSchedule}</span>
                        </button>
                    )}

                    {onPay && plan.status === "active" && (
                        <button type="button" className="plan-card__pay" onClick={onPay}>
                            {t.sales.addPayment}
                        </button>
                    )}
                </div>
            )}
        </article>
    );
}
